import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {UserCardResponse} from "../interface/userCardResponse";
import {SlickCarouselComponent} from "ngx-slick-carousel";
import {UserCardService} from "../service/user-card.service";
import {MatDialog, MatDialogRef} from "@angular/material/dialog";
import {DeleteProfileCardComponent} from "../profile-card/delete-profile-card/delete-profile-card.component";
import {ImageType} from "../enums/image-type-enum";
import {ViewStateService} from "../service/view-state.service";
import {UserCardResponseWrap} from "../interface/userCardResponseWrap";
import {HeaderFactoryService} from "../header/header-factory.service";
import {DELETE_CARD_TITLE, DELETE_DESCRIPTION} from "../constants/deleteProfileCards.constant";
import {Team} from "../interface/team";
import {SubscriptionResponse} from "../interface/subscriptionResponse";

import {TranslateService} from "@ngx-translate/core";
import {SharedConstants} from "../utilityClass/sharedConstants";
import {HttpErrorResponse} from "@angular/common/http";
import {SnackBarUtil} from "../qr-copy-message/snack-bar-util.service";
import {MockupDataModel} from "../qr-copy-message/mockup-data.model";

@Component({
  selector: 'app-carousel',
  templateUrl: './carousel.component.html',
  styleUrls: ['./carousel.component.scss',
    '../header/header.component.scss',
    '../teams/teams.component.scss',
    '../teams/teams.component-media.scss'],
})
export class CarouselComponent implements OnInit, OnChanges {

  @Input() userCards: UserCardResponse[] | any
  @Input() username: string | any
  @Input() sliderLength: number | any
  @Input() reservedIdentificationId: string | null = null;
  @Input() qrTeamWrappedCards: UserCardResponseWrap[] = [];
  @Input() qrTeamForUi?: Team;
  @Input() qrUserCreatedTeam: Team | null = null;
  @Input() qrTeams: Team[] = [];
  @Input() qrActiveTeamIndex: number | null = null;
  @Input() qrActiveSubscription: SubscriptionResponse | undefined;
  @Input() qrCurrentUserId: bigint | null = null;
  @Input() qrCurrentUserEmail: string | null = null;

  @ViewChild('slickModal') slickModal!: SlickCarouselComponent;

  @Output() deleteCard = new EventEmitter<bigint>();
  @Output() activeReservedQrCard = new EventEmitter<string>();

  clicked: boolean = false;
  dialogRef: MatDialogRef<DeleteProfileCardComponent> | undefined;

  isListView: boolean = false;
  isFavoriteMap: { [key: string]: boolean } = {};

  wrappedIndividualCards: UserCardResponseWrap[] = [];

  selectedCardId: string | null = null;

  protected readonly ImageType = ImageType;

  constructor(
    private dialog: MatDialog,
    private userCardService: UserCardService,
    private viewStateService: ViewStateService,
    private translateService: TranslateService,
    private snackBar: SnackBarUtil
    ) {
  }

  public getTranslatedTeamName(teamName: string): string {
    return teamName === SharedConstants.MY_TEAM
      ? this.translateService.instant('my_team.my_team')
      : teamName;
  }

  ngOnInit(): void {
    this.getCardsViewFromHeader()
    this.loadIndividualsCard()
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['userCards']) {
      this.loadIndividualsCard();
    }
  }

  loadIndividualsCard() {
    if (Array.isArray(this.userCards)) {
      this.wrappedIndividualCards = this.userCards.map((card: UserCardResponse) => ({
        userCardResponse: card
      }));
    }
  }

  onToggleChange(userId: bigint): void {
    this.userCardService.updateStatus(userId).subscribe(() => {
      const userCardById = this.wrappedIndividualCards.find((card: UserCardResponseWrap) => card.userCardResponse.id === userId);
      if (userCardById) {
        userCardById.userCardResponse.active = !userCardById.userCardResponse.active;
      }
    })
  }

  confirmDelete(id: bigint) {
    this.dialogRef = this.dialog.open(DeleteProfileCardComponent, {
      data: {
        deleteTitle: DELETE_CARD_TITLE,
        deleteDescription: DELETE_DESCRIPTION,
      }
    });
    this.dialogRef.afterClosed().subscribe((result: any) => {
      this.clicked = false;
      if (result) {
        this.userCardService.deleteCardById(id).subscribe({
          next: () => {
            this.wrappedIndividualCards = this.wrappedIndividualCards.filter(card => card.userCardResponse.id !== id);
            this.deleteCard.emit(id);
            this.updateCardsLengthForHeader();
          },
          error: (err: HttpErrorResponse) => {
            if (err.status === 409) {
              const message = err.error?.message ?? 'This card cannot be deleted because it is linked to an active Menu or Fee.';
              this.snackBar.openSnackBar({
                data: MockupDataModel.error(message),
                duration: 4000,
                panelClass: SnackBarUtil.CLASSES.error,
                horizontalPosition: 'center',
                verticalPosition: 'top'
              });
            }
          }
        });
      }
    });
  }

  onActiveReservedQrCard(cardId: bigint): void {
    if (this.userCards) {
      this.userCards.forEach((card: UserCardResponse) => {
        card.isReservedCardActive = (card.id === cardId);
      });
    }
    this.qrTeamWrappedCards.forEach(w => {
      w.userCardResponse.isReservedCardActive = (w.userCardResponse.id === cardId);
    });
    const cardIdStr = cardId.toString();
    this.selectedCardId = cardIdStr;
    this.activeReservedQrCard.emit(cardIdStr);
  }

  qrIsDeleteButtonActive(userCardResponse: UserCardResponse): boolean {
    if (this.qrUserCreatedTeam === null) {
      return false;
    }
    if (this.qrActiveTeamIndex !== this.qrUserCreatedTeam.id) {
      return false;
    }
    return (userCardResponse.userId !== this.qrCurrentUserId || !!userCardResponse.isDuplicated);
  }

  qrIsEditSectionVisible(userCardResponse: UserCardResponse): boolean {
    if (this.qrUserCreatedTeam !== null) {
      if (this.qrActiveTeamIndex === this.qrUserCreatedTeam.id) {
        return true;
      }
      return (userCardResponse.userId === this.qrCurrentUserId);
    }
    return (userCardResponse.userId === this.qrCurrentUserId);
  }

  qrIsAdminCard(userId: bigint): boolean {
    return this.qrIsOwnerOfActiveTeam(userId) || this.qrIsOwnerOfUserCreatedTeam(userId);
  }

  updateCardsLengthForHeader() {
    HeaderFactoryService.cardsCount.next(this.wrappedIndividualCards.length);
  }

  getCardsViewFromHeader() {
    this.viewStateService.isListView$.subscribe(isListView => {
      this.isListView = isListView;
    });
  }

  private qrIsOwnerOfActiveTeam(userId: bigint): boolean {
    return this.qrTeams.some((team: Team) => {
      return this.qrActiveTeamIndex === team.id && team.owner.id === userId;
    });
  }

  private qrIsOwnerOfUserCreatedTeam(userId: bigint): boolean {
    return (
      this.qrUserCreatedTeam !== null &&
      this.qrUserCreatedTeam.owner.id === userId &&
      this.qrUserCreatedTeam.id === this.qrActiveTeamIndex
    );
  }
}
